import type { YandexMetrikaGoal, YandexMetrikaHit } from '@/api/types'

export function useYandexMetrika() {
  const COUNTER_ID = 104180066

  const initYandexMetrika = () => {
    // Проверяем, что мы в браузере
    if (typeof window === 'undefined') return
    
    // Проверяем, что скрипт еще не загружен
    if (window.ym) {
      console.log('Yandex Metrika already initialized')
      return
    }

    console.log('Initializing Yandex Metrika...')

    // Создаем глобальную функцию сразу
    window.ym = window.ym || function() {
      (window.ym.a = window.ym.a || []).push(arguments)
    }
    window.ym.l = 1 * new Date()

    // Создаем скрипт
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://mc.yandex.ru/metrika/tag.js'
    
    // Обработчик загрузки скрипта
    script.onload = () => {
      console.log('Yandex Metrika script loaded successfully')
      
      // Инициализируем метрику
      window.ym(COUNTER_ID, 'init', {
        ssr: true,
        clickmap: true,
        ecommerce: "dataLayer",
        accurateTrackBounce: true,
        trackLinks: true,
        webvisor: true
      })
    }

    script.onerror = () => {
      console.error('Failed to load Yandex Metrika script')
    }

    // Добавляем скрипт в head
    document.head.appendChild(script)

    // Добавляем noscript fallback
    const noscript = document.createElement('noscript')
    const img = document.createElement('div')
    img.innerHTML = `<img src="https://mc.yandex.ru/watch/${COUNTER_ID}" style="position:absolute; left:-9999px;" alt="" />`
    noscript.appendChild(img)
    document.head.appendChild(noscript)
  }

  // Функция для отправки целей
  const reachGoal = (target: string, params?: any) => {
    if (typeof window !== 'undefined' && window.ym) {
      try {
        window.ym(COUNTER_ID, 'reachGoal', target, params)
        console.log(`Yandex Metrika goal reached: ${target}`, params)
      } catch (error) {
        console.error('Error sending Yandex Metrika goal:', error)
      }
    } else {
      console.warn('Yandex Metrika not initialized, goal not sent:', target)
    }
  }

  // Функция для отправки хитов (переходов по страницам)
  const hit = (url: string, params?: any) => {
    if (typeof window !== 'undefined' && window.ym) {
      try {
        window.ym(COUNTER_ID, 'hit', url, params)
        console.log(`Yandex Metrika hit sent: ${url}`, params)
      } catch (error) {
        console.error('Error sending Yandex Metrika hit:', error)
      }
    } else {
      console.warn('Yandex Metrika not initialized, hit not sent:', url)
    }
  }

  // Предустановленные цели для вашего проекта
  const trackEvent = {
    // Основные действия
    contactFormOpen: () => reachGoal('contact_form_open'),
    contactFormSubmit: (data?: any) => reachGoal('contact_form_submit', data),
    
    // События
    eventView: (eventId: number) => reachGoal('event_view', { event_id: eventId }),
    eventListView: (source?: string) => reachGoal('event_list_view', { source }),
    eventRegister: (eventId: number) => reachGoal('event_register', { event_id: eventId }),
    
    // Партнеры
    partnerClick: (partnerId: number) => reachGoal('partner_click', { partner_id: partnerId }),
    
    // Навигация
    headerMenuClick: (section: string) => reachGoal('header_menu_click', { section }),
    footerClick: (link: string) => reachGoal('footer_click', { link }),
    
    // Медиа проекты
    mediaProjectClick: (project: string) => reachGoal('media_project_click', { project }),
    
    // YCM события
    ycmEventView: () => reachGoal('ycm_events_view'),
    
    // Поиск
    searchPerformed: (query: string, results: number) => reachGoal('search_performed', { 
      query, 
      results_count: results 
    }),
    
    // Ошибки
    formError: (form: string, error: string) => reachGoal('form_error', { form, error })
  }

  return {
    initYandexMetrika,
    reachGoal,
    hit,
    trackEvent
  }
}